import { ChevronRight } from '#app/components/icons/solid'
import {
	type TreeItemContentProps,
	type TreeItemContentRenderProps,
	type TreeItemProps,
	Button,
	TreeItemContent,
	TreeItem as TreeItemPrimitive,
	Tree,
} from 'react-aria-components'

import { Checkbox } from '#app/components/base/checkbox/checkbox.tsx'
import { cx } from '#app/utils/cx'

const TreeItem = <T extends object>({
	className,
	...props
}: TreeItemProps<T>) => {
	return (
		<TreeItemPrimitive
			className={(renderProps) =>
				cx(
					'group/tree-item relative flex cursor-pointer items-center rounded-md px-2.5 py-2 transition duration-100 ease-linear',
					'outline-focus-ring select-none focus:outline-hidden',
					'hover:bg-primary_hover focus-visible:bg-primary_hover',
					'data-[dragging]:bg-transparent data-[dragging]:shadow-none',
					'focus-visible:outline-2 focus-visible:-outline-offset-2',
					'*:data-[slot=icon]:mr-2 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:stroke-[2.25px]',
					'href' in props ? 'cursor-pointer' : 'cursor-default',
					typeof className === 'function' ? className(renderProps) : className,
				)
			}
			{...props}
		/>
	)
}

interface TreeContentProps extends TreeItemContentProps {
	className?: string
}

const TreeContent = ({ className, children, ...props }: TreeContentProps) => {
	return (
		<TreeItemContent {...props}>
			{(values) => (
				<div
					className={cx(
						'text-secondary relative flex w-full min-w-0 items-center text-sm font-semibold',
						'group-hover/tree-item:text-secondary_hover group-focus-visible/tree-item:text-secondary_hover',
						className,
					)}
				>
					{values.selectionMode === 'multiple' &&
						values.selectionBehavior === 'toggle' && (
							<Checkbox className="mr-2 shrink-0" slot="selection" />
						)}
					<div
						className={cx(
							'relative w-[calc(calc(var(--tree-item-level)-1)*calc(var(--spacing)*5.5))] shrink-0',
							'before:absolute before:inset-0 before:-ms-1 before:bg-[repeating-linear-gradient(to_right,transparent_0,transparent_calc(var(--tree-item-level)-1px),var(--color-border-primary)_calc(var(--tree-item-level)-1px),var(--color-border-primary)_calc(var(--tree-item-level)))]',
						)}
					/>
					{values.hasChildItems ? (
						<TreeIndicator
							values={{
								isDisabled: values.isDisabled,
								isExpanded: values.isExpanded,
							}}
						/>
					) : (
						<span className="mr-2 block size-4 shrink-0" />
					)}
					{typeof children === 'function' ? children(values) : children}
				</div>
			)}
		</TreeItemContent>
	)
}

const TreeIndicator = ({
	values,
}: {
	values: Pick<TreeItemContentRenderProps, 'isDisabled' | 'isExpanded'>
}) => {
	return (
		<Button
			slot="chevron"
			isDisabled={values.isDisabled}
			className={cx(
				'mr-2 size-4 shrink-0 cursor-pointer rounded-sm transition duration-100 ease-linear',
				'text-fg-quaternary hover:text-fg-quaternary_hover focus-visible:text-fg-quaternary_hover',
				'outline-focus-ring focus:outline-hidden focus-visible:outline-2 focus-visible:-outline-offset-2',
				values.isExpanded && 'text-fg-secondary',
				values.isDisabled && 'text-fg-disabled cursor-not-allowed',
			)}
		>
			<ChevronRight
				className={cx(
					'size-4 stroke-[2.25px] transition-transform duration-100 ease-linear',
					values.isExpanded && 'rotate-90',
				)}
			/>
		</Button>
	)
}

TreeItem.displayName = 'TreeItem'
TreeContent.displayName = 'TreeContent'
TreeIndicator.displayName = 'TreeIndicator'

export { Tree, TreeItem, TreeContent }
